import React from 'react';
import { Dayjs } from 'dayjs';
import dayjs from 'dayjs';
import { Modal, Button, Timeline, Tag, Empty, Divider, Space } from 'antd';
import {
  ClockCircleOutlined,
  UserOutlined,
  CarOutlined,
  PlusOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined
} from '@ant-design/icons';
import { Appointment } from '../../services/appointmentApi';
import './EmployeeDetailModal.css';

interface Employee {
  id: number;
  first_name: string;
  last_name: string;
  role: string;
}

interface TechnicianSchedule {
  id: number;
  user_id: number;
  day_of_week: number;
  start_time: string;
  end_time: string;
  is_active: boolean;
}

interface ScheduleException {
  id: number;
  user_id: number;
  exception_type: 'time_off' | 'break' | 'special_hours' | 'holiday';
  start_datetime: string;
  end_datetime: string;
  reason?: string;
}

interface TimeSlotItem {
  time: string;
  hour: number;
  minute: number;
  type: 'available' | 'booked' | 'unavailable' | 'break';
  appointment?: Appointment;
  reason?: string;
}

interface EmployeeDetailModalProps {
  visible: boolean;
  employee: Employee;
  date: Dayjs;
  appointments: Appointment[];
  schedule: TechnicianSchedule[];
  exceptions: ScheduleException[];
  onClose: () => void;
  onAppointmentClick: (appointment: Appointment) => void;
  onScheduleApprovedEstimate: () => void;
}

export const EmployeeDetailModal: React.FC<EmployeeDetailModalProps> = ({
  visible,
  employee,
  date,
  appointments,
  schedule,
  exceptions,
  onClose,
  onAppointmentClick,
  onScheduleApprovedEstimate
}) => {
  const employeeName = `${employee.first_name} ${employee.last_name}`;
  const dayOfWeek = date.day();
  const todaySchedule = schedule.find(s => s.day_of_week === dayOfWeek && s.is_active);

  // Generate hourly time slots
  const generateTimeSlots = (): TimeSlotItem[] => {
    if (!todaySchedule) return [];

    const slots: TimeSlotItem[] = [];
    const [startHour] = todaySchedule.start_time.split(':').map(Number);
    const [endHour] = todaySchedule.end_time.split(':').map(Number);

    for (let hour = startHour; hour <= endHour; hour++) {
      const timeStr = `${hour.toString().padStart(2, '0')}:00`;
      const slotDateTime = date.hour(hour).minute(0);

      // Check if there's an appointment at this time
      const appointment = appointments.find(apt => {
        const aptStart = dayjs(apt.start_time);
        const aptEnd = dayjs(apt.end_time);
        return (slotDateTime.isAfter(aptStart) || slotDateTime.isSame(aptStart)) && slotDateTime.isBefore(aptEnd);
      });

      // Check if there's an exception (break, time off)
      const exception = exceptions.find(exc => {
        const excStart = dayjs(exc.start_datetime);
        const excEnd = dayjs(exc.end_datetime);
        return (slotDateTime.isAfter(excStart) || slotDateTime.isSame(excStart)) && slotDateTime.isBefore(excEnd);
      });

      if (appointment) {
        slots.push({
          time: timeStr,
          hour,
          minute: 0,
          type: 'booked',
          appointment
        });
      } else if (exception) {
        slots.push({
          time: timeStr,
          hour,
          minute: 0,
          type: exception.exception_type === 'break' ? 'break' : 'unavailable',
          reason: exception.reason || exception.exception_type.replace('_', ' ').toUpperCase()
        });
      } else {
        slots.push({
          time: timeStr,
          hour,
          minute: 0,
          type: 'available'
        });
      }
    }

    return slots;
  };

  const timeSlots = generateTimeSlots();

  // Get timeline item color
  const getTimelineColor = (type: string): string => {
    switch (type) {
      case 'available': return 'green';
      case 'booked': return 'blue';
      case 'break': return 'orange';
      case 'unavailable': return 'red';
      default: return 'gray';
    }
  };

  // Get timeline item icon
  const getTimelineIcon = (type: string) => {
    switch (type) {
      case 'available': return <CheckCircleOutlined style={{ fontSize: '16px' }} />;
      case 'booked': return <ClockCircleOutlined style={{ fontSize: '16px' }} />;
      case 'break': return <CloseCircleOutlined style={{ fontSize: '16px' }} />;
      case 'unavailable': return <CloseCircleOutlined style={{ fontSize: '16px' }} />;
      default: return <ClockCircleOutlined style={{ fontSize: '16px' }} />;
    }
  };

  return (
    <Modal
      title={
        <Space direction="vertical" size={0}>
          <span style={{ fontSize: '18px', fontWeight: 700 }}>
            {employeeName} - {date.format('dddd, MMM D')}
          </span>
          <Tag color={employee.role === 'owner' ? 'purple' : 'blue'}>
            {employee.role.toUpperCase()}
          </Tag>
        </Space>
      }
      open={visible}
      onCancel={onClose}
      width={700}
      centered
      footer={[
        <Button key="close" onClick={onClose}>
          Close
        </Button>,
        <Button
          key="schedule"
          type="primary"
          icon={<PlusOutlined />}
          onClick={onScheduleApprovedEstimate}
          size="large"
        >
          Schedule Approved Estimate
        </Button>
      ]}
      className="employee-detail-modal"
    >
      {!todaySchedule ? (
        <Empty
          description={`${employeeName} is not scheduled to work on ${date.format('dddd')}`}
          image={Empty.PRESENTED_IMAGE_SIMPLE}
        />
      ) : (
        <>
          {/* Working Hours */}
          <div className="working-hours-section">
            <div className="working-hours-item">
              <ClockCircleOutlined /> Working Hours: <strong>{todaySchedule.start_time} - {todaySchedule.end_time}</strong>
            </div>
            {exceptions.filter(e => e.exception_type === 'break').map(exc => (
              <div key={exc.id} className="working-hours-item">
                <CloseCircleOutlined /> {exc.reason || 'Break'}: <strong>
                  {dayjs(exc.start_datetime).format('h:mm A')} - {dayjs(exc.end_datetime).format('h:mm A')}
                </strong>
              </div>
            ))}
          </div>

          <Divider />

          {/* Timeline */}
          <div className="schedule-timeline">
            <Timeline mode="left">
              {timeSlots.map((slot, index) => (
                <Timeline.Item
                  key={index}
                  color={getTimelineColor(slot.type)}
                  dot={getTimelineIcon(slot.type)}
                  label={<span style={{ fontSize: '15px', fontWeight: 600 }}>{slot.time}</span>}
                >
                  {slot.type === 'available' && (
                    <div className="timeline-item-content available">
                      <Tag color="green">AVAILABLE</Tag>
                      <span>Open slot</span>
                    </div>
                  )}

                  {slot.type === 'booked' && slot.appointment && (
                    <div
                      className="timeline-item-content booked"
                      onClick={() => onAppointmentClick(slot.appointment!)}
                      style={{ cursor: 'pointer' }}
                    >
                      <Tag color="blue">BOOKED</Tag>
                      <div className="appointment-info">
                        <div className="appointment-title">
                          {slot.appointment.title || slot.appointment.appointment_type}
                        </div>
                        {slot.appointment.customer && (
                          <div className="appointment-customer">
                            <UserOutlined /> {slot.appointment.customer.first_name} {slot.appointment.customer.last_name}
                          </div>
                        )}
                        {slot.appointment.vehicle && (
                          <div className="appointment-vehicle">
                            <CarOutlined /> {slot.appointment.vehicle.year} {slot.appointment.vehicle.make} {slot.appointment.vehicle.model}
                          </div>
                        )}
                      </div>
                    </div>
                  )}

                  {slot.type === 'break' && (
                    <div className="timeline-item-content break">
                      <Tag color="orange">BREAK</Tag>
                      <span>{slot.reason}</span>
                    </div>
                  )}

                  {slot.type === 'unavailable' && (
                    <div className="timeline-item-content unavailable">
                      <Tag color="red">UNAVAILABLE</Tag>
                      <span>{slot.reason}</span>
                    </div>
                  )}
                </Timeline.Item>
              ))}
            </Timeline>
          </div>
        </>
      )}
    </Modal>
  );
};
